import { Button } from "@studyplus/boron-ui";
import classNames from "classnames";
import { FormikHelpers, FormikProps, useFormik } from "formik";
import truncate from "lodash-es/truncate";
import { ChangeEvent, FC, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select, { OnChangeValue } from "react-select";
import Icon from "../../../components/atoms/Icon";
import Input from "../../../components/atoms/Input";
import InputIcon from "../../../components/atoms/InputIcon";
import { MainSection } from "../../../components/atoms/MainSection";
import ResumableTextarea from "../../../components/atoms/ResumableTextarea";
import {
  generateStorageKey,
  useResumableTextarea,
} from "../../../components/atoms/ResumableTextarea/useResumableTextarea";
import DeprecatedTagButton from "../../../components/features/DeprecatedTagButton";
import { UnprocessableEntityError } from "../../../errors";
import { useFetchKarteTemplates } from "../../../hooks/http/useFetchKarteTemplates";
import { useQueryError } from "../../../hooks/http/useQueryError";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
import { ApiErrorInterface } from "../../../interfaces/ApiErrorResponseInterface";
import { KarteTemplateInterface } from "../../../interfaces/KarteTemplateInterface";
import OperatorInterface from "../../../interfaces/OperatorInterface";
import StudentInterface from "../../../interfaces/StudentInterface";
import styleVars from "../../../styles/variables.scss";
import styles from "./styles.scss";
import { usePostStudentKarte } from "./usePostStudentKarte";
import validationSchema from "./validationSchema";

interface Props {
  student: StudentInterface;
  operator: OperatorInterface | null;
}

interface Values {
  name: string;
  comment: string;
  karteImageAttachments?: File[];
  karteAttachments?: File[];
}

const templateOptions = (karteTemplates: KarteTemplateInterface[]) => {
  return karteTemplates.map((template) => {
    return {
      label: template.name,
      value: template.template,
    };
  });
};

const customStyles = {
  control: (provided: any) => ({
    ...provided,
    height: "4.6rem",
    border: `1px solid ${styleVars.colorGrayLighten1}`,
  }),
};

const StudentKarteForm: FC<Props> = (props) => {
  const [apiErrors, setApiErrors] = useState<ApiErrorInterface[]>([]);

  const { showSuccessMessage, showErrorMessage } = useFlashMessage();

  const {
    data: karteTemplates,
    isLoading: isLoadingTemplates,
    error,
  } = useFetchKarteTemplates({ sectionId: props.student.section.id });
  useQueryError(error);

  const values = mapPropsToValues(props);

  const { clearValue: clearCommentStorage, ...resumableTextareaProps } =
    useResumableTextarea({
      value: values.comment,
      key: generateStorageKey(["kartes", props.student.id, "new"]),
      confirmMessage:
        "編集途中のカルテを復元しますか？\n（「キャンセル」を選択すると復元内容が破棄されます。）",
    });

  const { mutate: postKarte } = usePostStudentKarte({
    studentId: props.student.id,
  });

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      const params = valuesToParams(values);
      postKarte(params, {
        onSettled: () => {
          formikHelpers.setSubmitting(false);
        },
        onSuccess: () => {
          clearCommentStorage();
          formikHelpers.resetForm();
          showSuccessMessage("カルテを作成しました");
          setApiErrors([]);
        },
        onError: (error) => {
          showErrorMessage("カルテを作成できませんでした");
          if (error instanceof UnprocessableEntityError) {
            setApiErrors(error.originalErrors);
          }
        },
      });
    },
    [postKarte, clearCommentStorage, showSuccessMessage, showErrorMessage],
  );

  const formik = useFormik<Values>({
    initialValues: values,
    validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    const { isResumed, resumedValue } = resumableTextareaProps;
    if (isResumed && resumedValue) {
      formik.getFieldHelpers("comment").setValue(resumedValue);
    }
  }, [resumableTextareaProps.isResumed, resumableTextareaProps.resumedValue]);

  const isDisabled = !formik.isValid || formik.isSubmitting;

  return (
    <div className={styles.root} aria-label="カルテ投稿フォーム">
      <MainSection>
        <div className={styles.setting}>
          <Link
            to={`/sections/${props.student.section.id}/settings/karte_templates`}
            className={styles.setting__link}
          >
            <Icon name="icon-karte" className={styles.setting__icon} />
            設定
          </Link>
        </div>
        <form className={styles.form} onSubmit={formik.handleSubmit}>
          <div className={styles.header}>
            <div className={styles.staffName}>
              <label
                htmlFor={"StudentKarteForm-staffName-input"}
                className={styles.label}
              >
                スタッフ名
              </label>
              {renderStaffNameError(formik)}
              <Input
                id="StudentKarteForm-staffName-input"
                name="name"
                className={classNames(styles.staffName__input, {
                  [styles.fieldError]: shouldDisplayError(formik, "name"),
                })}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>

            <div className={styles.template}>
              <label
                htmlFor={"StudentKarteForm-template-options"}
                className={styles.label}
              >
                テンプレート
              </label>
              <br />
              <Select
                options={templateOptions(karteTemplates || [])}
                isLoading={isLoadingTemplates}
                placeholder="テンプレートを選択"
                onChange={handleChangeTemplate(formik)}
                isSearchable={true}
                noOptionsMessage={() => ""}
                styles={customStyles}
              />
            </div>
          </div>

          <div className={styles.body}>
            <div className={styles.comment}>
              <label
                htmlFor={"StudentKarteForm-comment-textarea"}
                className={styles.label}
              >
                コメント
              </label>
              {renderCommentError(formik)}
              <ResumableTextarea
                id="StudentKarteForm-comment-textarea"
                name="comment"
                className={classNames(styles.comment__input, {
                  [styles.fieldError]: shouldDisplayError(formik, "comment"),
                })}
                rows={3}
                placeholder="コメントを入力"
                value={formik.values.comment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                {...resumableTextareaProps}
              />
            </div>
          </div>

          {renderApiErrors(apiErrors)}

          <div className={styles.footer}>
            {renderAttachments(props, formik)}
            <Button type="submit" disabled={isDisabled}>
              登録
            </Button>
          </div>
        </form>
      </MainSection>
    </div>
  );
};

const renderAttachments = (props: Props, formik: FormikProps<Values>) => {
  if (props.student.canCreateKarteAttachment) {
    return (
      <>
        <div className={styles.attachments}>
          {renderImageNames(formik)}
          {renderAttachmentNames(formik)}
        </div>
        <InputIcon
          iconName="icon-picture-img"
          inputAttributes={{
            accept: "image/jpeg,image/gif,image/png",
            id: "karte_image_attachments_attributes",
            type: "file",
          }}
          className={styles.footer__icon}
          tooltipLabel="画像の追加( jpg,png,gif )"
          onChange={handleChangeImageAttachment(formik)}
        />
        <InputIcon
          iconName="icon-file"
          inputAttributes={{
            id: "karte_attachments_attributes",
            type: "file",
          }}
          className={styles.footer__icon}
          tooltipLabel="ファイルの追加( PDF,Word,Excel,PPT )"
          onChange={handleChangeAttachment(formik)}
        />
      </>
    );
  } else {
    return null;
  }
};

const renderAttachmentNames = (formik: FormikProps<Values>) => {
  const { karteAttachments } = formik.values;

  if (karteAttachments) {
    return karteAttachments.map((attachment: File, i: number) => {
      return (
        <div
          className={styles.filename}
          key={`StudentKarteForm-attachmentNames-${i}`}
        >
          <DeprecatedTagButton
            label={truncateFilename(attachment.name, 30)}
            iconName="icon-close-x"
            theme="lightgray"
            onClick={handleAttachmentDelete(formik, i)}
          />
        </div>
      );
    });
  }
};

const renderImageNames = (formik: FormikProps<Values>) => {
  const { karteImageAttachments } = formik.values;

  if (karteImageAttachments) {
    return karteImageAttachments.map((imageAttachment: File, i: number) => {
      return (
        <div
          className={styles.filename}
          key={`StudentKarteForm-imageNames-${i}`}
        >
          <DeprecatedTagButton
            label={truncateFilename(imageAttachment.name, 30)}
            iconName="icon-close-x"
            theme="lightgray"
            onClick={handleImageAttachmentDelete(formik, i)}
          />
        </div>
      );
    });
  }
};

const renderApiErrors = (apiErrors: ApiErrorInterface[]) => {
  return (
    <div className={styles.apiErrors}>
      {apiErrors.map((error, i: number) => {
        return (
          <p
            key={`StudentKarteForm-apiErrors-${i}`}
            className={styles.apiErrors__error}
          >
            {error.message}
          </p>
        );
      })}
    </div>
  );
};

const renderStaffNameError = (formik: FormikProps<Values>) => {
  if (shouldDisplayError(formik, "name")) {
    return <p className={styles.staffName__error}>{formik.errors.name}</p>;
  } else {
    return null;
  }
};

const renderCommentError = (formik: FormikProps<Values>) => {
  if (shouldDisplayError(formik, "comment")) {
    return <p className={styles.staffName__error}>{formik.errors.comment}</p>;
  } else {
    return null;
  }
};

const handleAttachmentDelete =
  (formik: FormikProps<Values>, index: number) => () => {
    const { setFieldValue } = formik;
    if (formik.values.karteAttachments) {
      formik.values.karteAttachments.splice(index, 1);
      setFieldValue(`karteAttachments`, formik.values.karteAttachments);
    }
  };

const handleImageAttachmentDelete =
  (formik: FormikProps<Values>, index: number) => () => {
    const { setFieldValue } = formik;
    if (formik.values.karteImageAttachments) {
      formik.values.karteImageAttachments.splice(index, 1);
      setFieldValue(
        `karteImageAttachments`,
        formik.values.karteImageAttachments,
      );
    }
  };

const handleChangeAttachment =
  (formik: FormikProps<Values>) => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    const currentIndex = formik.values.karteAttachments
      ? formik.values.karteAttachments.length
      : 0;
    formik.setFieldValue(`karteAttachments[${currentIndex}]`, file);
  };

const handleChangeTemplate =
  (formik: FormikProps<Values>) =>
  (value: OnChangeValue<ReturnType<typeof templateOptions>[number], false>) => {
    if (!formik.values.comment) {
      formik.setFieldValue("comment", value?.value);
      return;
    }

    if (window.confirm("コメント欄を上書きしてもよろしいですか？")) {
      formik.setFieldValue("comment", value?.value);
    }
  };

const handleChangeImageAttachment =
  (formik: FormikProps<Values>) => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    const currentIndex = formik.values.karteImageAttachments
      ? formik.values.karteImageAttachments.length
      : 0;
    formik.setFieldValue(`karteImageAttachments[${currentIndex}]`, file);
  };

const mapPropsToValues = (props: Props): Values => {
  return {
    name: props.operator ? props.operator.fullName : "",
    comment: "",
  };
};

const valuesToParams = (values: Values): any => {
  const params: any = {
    name: values.name,
    comment: values.comment,
  };

  if (values.karteImageAttachments) {
    params.karte_image_attachments_attributes =
      values.karteImageAttachments.map((imageAttachment: File) => ({
        file: imageAttachment,
      }));
  }

  if (values.karteAttachments) {
    params.karte_attachments_attributes = values.karteAttachments.map(
      (attachment: File) => ({
        file: attachment,
      }),
    );
  }

  return params;
};

const shouldDisplayError = (
  formik: FormikProps<Values>,
  key: string,
): boolean => {
  const meta = formik.getFieldMeta(key);
  const { touched, error } = meta;

  return touched && !!error;
};

const truncateFilename = (filename: string, length: number): string => {
  if (filename.length <= length) {
    return filename;
  }

  const extension = filename
    .substring(filename.lastIndexOf(".") + 1, filename.length)
    .toLowerCase();
  const filenameWithoutExt = filename.replace("." + extension, "");
  const truncatedFileName = truncate(filenameWithoutExt, {
    length: length - extension.length,
  });

  return truncatedFileName + extension;
};

export default StudentKarteForm;
